import { Stack, Typography } from "@mui/material";
import { LoadedContent } from "components/common/loading/loadedContent";
import { NotFound } from "components/notFound";
import {
  COMPLIANCE_DATE_RANGE_CONFIG,
  DateRangeProvider,
  DELIVERIES_DATE_RANGE_CONFIG,
  DEFAULT_DATE_RANGE_CONFIG,
  SCHEDULE_DATE_RANGE_CONFIG,
  useDateRange,
} from "contexts/DateRangeContext";
import { useTenantData } from "contexts/tenant";
import dayjs from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useHasPermission } from "hooks/permissions";
import { useTenant } from "hooks/settings";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { useTabNavigation } from "hooks/useTabNavigation";
import { ReactNode, useEffect, useMemo } from "react";
import { sendVisitNotification } from "src/utils/sendVisitNotification";
import {
  FeatureFlag,
  Permission,
  generatedApi,
} from "src/store/api/generatedApi";
import { useAppDispatch } from "src/store/store";
import { CompliancePage } from "./compliance/CompliancePage";
import { ConsumptionTab } from "./consumption/ConsumptionTab";
import { CostTab } from "./cost";
import { useStrategyDashboardCost } from "./cost/costHooks";
import { CrewComparisonTab } from "./crewComparison/CrewComparisonTab";
import { DeliveriesTab } from "./deliveries/DeliveriesTab";
import { useStrategyDashboardEditing } from "./editing/editingHooks";
import { EditingTab } from "./editing/EditingTab";
import { useStrategyDashboardFailureRate } from "./failureRate/failureRateHooks";
import { QualityTab } from "./failureRate/QualityTab";
import { HeatsTab } from "./heats/HeatsTab";
import { useStrategyDashboardInventory } from "./inventory/inventoryHooks";
import { InventoryTab } from "./inventory/InventoryTab";
import { useStrategyDashboardPrices } from "./prices/pricesHooks";
import { PricesTab } from "./prices/PricesTab";
import { useStrategyDashboardProduction } from "./production/productionHooks";
import { ProductionTab } from "./production/ProductionTab";
import { ScrapChefUsageTab } from "./scrapChefUsage/ScrapChefUsageTab";
import { useStrategyDashboardScrapChefUsage } from "components/strategyDashboard/scrapChefUsage/scrapChefUsageHooks";
import { SavingsTab } from "./savings/SavingsTab";
import { useStrategyDashboardSavings } from "./savings/savingsHooks";
import { useStrategyDashboardSchedule } from "./schedule/scheduleHooks";
import { ScheduleTab } from "./schedule/ScheduleTab";

const LoadedInventoryTab = () => {
  const { startDate, endDate } = useDateRange();
  const start = startDate.format();
  const end = endDate.format();
  const data = useStrategyDashboardInventory(start, end);
  return (
    <LoadedContent data={data}>
      {(data) => <InventoryTab groupedInventorySeries={data} />}
    </LoadedContent>
  );
};
const LoadedProductionTab = () => {
  const { startDate, endDate } = useDateRange();
  const start = startDate.format();
  const end = endDate.format();
  const data = useStrategyDashboardProduction(start, end);
  return (
    <LoadedContent data={data}>
      {(data) => <ProductionTab groupedProductionSeries={data} />}
    </LoadedContent>
  );
};
const LoadedQualityTab = () => {
  const { startDate, endDate } = useDateRange();
  const start = startDate.format();
  const end = endDate.format();
  const data = useStrategyDashboardFailureRate(start, end);
  return (
    <LoadedContent data={data}>
      {(data) => <QualityTab groupedFailureRateSeries={data} />}
    </LoadedContent>
  );
};
const LoadedCostTab = () => {
  const { startDate, endDate } = useDateRange();
  const start = startDate.format();
  const end = endDate.format();
  const data = useStrategyDashboardCost(start, end);
  return (
    <LoadedContent data={data}>
      {(data) => <CostTab data={data} />}
    </LoadedContent>
  );
};
const LoadedPricesTab = () => {
  const { startDate, endDate } = useDateRange();
  const start = startDate.format();
  const end = endDate.format();
  const data = useStrategyDashboardPrices(start, end);
  return (
    <LoadedContent data={data}>
      {(data) => <PricesTab groupedPriceSeries={data} />}
    </LoadedContent>
  );
};
const LoadedScrapChefUsageTab = () => {
  const data = useStrategyDashboardScrapChefUsage();
  return (
    <LoadedContent data={data}>
      {(data) => <ScrapChefUsageTab groupedScrapChefUsageSeries={data} />}
    </LoadedContent>
  );
};
const LoadedSavingsTab = () => {
  const data = useStrategyDashboardSavings();
  return (
    <LoadedContent data={data}>
      {(data) => <SavingsTab data={data} />}
    </LoadedContent>
  );
};
const LoadedDeliveriesTab = () => {
  return <DeliveriesTab />;
};

const LoadedComplianceTab = () => {
  return <CompliancePage />;
};
const LoadedEditingTab = () => {
  const { startDate, endDate } = useDateRange();
  const start = startDate.format();
  const end = endDate.format();
  const data = useStrategyDashboardEditing(start, end);
  return (
    <LoadedContent data={data}>
      {(data) => <EditingTab data={data} />}
    </LoadedContent>
  );
};

const LoadedScheduleTab = () => (
  <DateRangeProvider config={SCHEDULE_DATE_RANGE_CONFIG}>
    <LoadedScheduleTabContent />
  </DateRangeProvider>
);

const LoadedScheduleTabContent = () => {
  const { startDate, endDate } = useDateRange();
  const start = startDate.format();
  const end = endDate.format();
  const data = useStrategyDashboardSchedule(start, end);
  return (
    <LoadedContent data={data}>
      {(data) => <ScheduleTab groupedScheduleSeries={data} />}
    </LoadedContent>
  );
};

const TAB_PREFETCH_DELAY_MS = 5000;

const usePrefetchStrategyDashboardData = ({
  selectedTabKey,
  visibleTabKeys,
}: {
  selectedTabKey: string | undefined;
  visibleTabKeys: string[];
}) => {
  const { startDate, endDate } = useDateRange();
  const start = startDate.format();
  const end = endDate.format();

  const tenantName = useTenant();
  const {
    mix_quantity: mixQuantity,
    compliance_page_version: compliancePageVersion,
  } = useTenantData();
  const { localTimezone } = useLocalDatetime();
  const dispatch = useAppDispatch();

  const complianceStart =
    mixQuantity === "grabs"
      ? start
      : COMPLIANCE_DATE_RANGE_CONFIG.defaultStartDate.format();
  const complianceEnd =
    mixQuantity === "grabs"
      ? end
      : COMPLIANCE_DATE_RANGE_CONFIG.defaultEndDate.format();
  const deliveriesStart =
    DELIVERIES_DATE_RANGE_CONFIG.defaultStartDate.format();
  const deliveriesEnd = DELIVERIES_DATE_RANGE_CONFIG.defaultEndDate.format();

  useEffect(() => {
    const toPrefetch = new Set(visibleTabKeys);
    if (selectedTabKey) {
      toPrefetch.delete(selectedTabKey);
    }

    const todayStart = dayjs().tz(localTimezone).startOf("day").format();
    const todayEnd = dayjs().tz(localTimezone).endOf("day").format();

    const prefetchers: Record<string, () => void> = {
      inventory: () => {
        dispatch(
          generatedApi.util.prefetch(
            "getStrategyDashboardInventory",
            { tenantName, start, end },
            { ifOlderThan: 60 }
          )
        );
      },
      consumption: () => {
        dispatch(
          generatedApi.util.prefetch(
            "getStrategyDashboardConsumption",
            { tenantName, start, end, timeAggregation: "monthly" },
            { ifOlderThan: 60 }
          )
        );
      },
      production: () => {
        dispatch(
          generatedApi.util.prefetch(
            "getStrategyDashboardProduction",
            { tenantName, start, end },
            { ifOlderThan: 60 }
          )
        );
      },
      quality: () => {
        dispatch(
          generatedApi.util.prefetch(
            "getStrategyDashboardFailureRate",
            { tenantName, start, end },
            { ifOlderThan: 60 }
          )
        );
      },
      cost: () => {
        dispatch(
          generatedApi.util.prefetch(
            "getStrategyDashboardCost",
            { tenantName, start, end, timeAggregation: "monthly" },
            { ifOlderThan: 60 }
          )
        );
      },
      prices: () => {
        dispatch(
          generatedApi.util.prefetch(
            "getStrategyDashboardPrices",
            { tenantName, start, end },
            { ifOlderThan: 60 }
          )
        );
      },
      scrapChefUsage: () => {
        dispatch(
          generatedApi.util.prefetch(
            "getStrategyDashboardScrapChefUsage",
            { tenantName },
            { ifOlderThan: 60 }
          )
        );
      },
      savings: () => {
        dispatch(
          generatedApi.util.prefetch(
            "getStrategyDashboardSavingsTimeseries",
            { tenantName },
            { ifOlderThan: 60 }
          )
        );
      },
      deliveries: () => {
        dispatch(
          generatedApi.util.prefetch(
            "getStrategyDashboardDeliveriesAndPurchases",
            { tenantName, start: deliveriesStart, end: deliveriesEnd },
            { ifOlderThan: 60 }
          )
        );
      },
      compliance: () => {
        if (compliancePageVersion === "modern") {
          return;
        }
        dispatch(
          generatedApi.util.prefetch(
            mixQuantity === "grabs"
              ? "getCrewAggregatedCompliance"
              : "getStrategyDashboardCompliance",
            {
              ...{
                tenantName,
                start: complianceStart,
                end: complianceEnd,
                ...(mixQuantity === "grabs" ? { includeReclaim: true } : {}),
              },
            },
            { ifOlderThan: 60 }
          )
        );
      },
      heats: () => {
        dispatch(
          generatedApi.util.prefetch(
            "getStrategyDashboardHeats",
            { tenantName, start: todayStart, end: todayEnd },
            { ifOlderThan: 60 }
          )
        );
      },
      schedule: () => {
        dispatch(
          generatedApi.util.prefetch(
            "getTenantSiopSchedule",
            {
              tenantName,
              includeDaily: true,
              fromDate:
                SCHEDULE_DATE_RANGE_CONFIG.defaultStartDate.format(
                  "YYYY-MM-DD"
                ),
            },
            { ifOlderThan: 60 }
          )
        );
      },
    };

    const timeoutId = setTimeout(() => {
      toPrefetch.forEach((key) => {
        const run = prefetchers[key];
        if (run) run();
      });
    }, TAB_PREFETCH_DELAY_MS);

    return () => {
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- selectedTabKey intentionally excluded: prefetch runs for OTHER tabs, re-running on tab switch defeats the purpose
  }, [
    tenantName,
    start,
    end,
    complianceStart,
    complianceEnd,
    compliancePageVersion,
    deliveriesStart,
    deliveriesEnd,
    visibleTabKeys,
    localTimezone,
    mixQuantity,
    dispatch,
    // selectedTabKey --  Do not include as it would make this run whenever tab changes
  ]);
};

// Component such that it is within DateRangeProvider
const PrefetchStrategyDashboardData = ({
  selectedTabKey,
  visibleTabKeys,
}: {
  selectedTabKey: string | undefined;
  visibleTabKeys: string[];
}) => {
  usePrefetchStrategyDashboardData({
    selectedTabKey,
    visibleTabKeys,
  });
  return null;
};

interface DashboardTab {
  key: string;
  title: string;
  content: ReactNode;
  featureFlag?: FeatureFlag;
  permissionsToCheck?: Permission[];
  ignoreCatchall?: boolean;
}

export const StrategyDashboardPage = () => {
  const { t } = useTenantTranslation();

  const tabSpecs = useMemo<DashboardTab[]>(
    () => [
      {
        key: "inventory",
        title: t("inventory"),
        featureFlag: "strategy_dashboard_inventory",
        permissionsToCheck: ["strategy_dashboard_inventory"],
        content: <LoadedInventoryTab />,
      },
      {
        key: "consumption",
        title: t("consumption"),
        featureFlag: "strategy_dashboard_consumption",
        permissionsToCheck: ["strategy_dashboard_consumption"],
        content: <ConsumptionTab />,
      },
      {
        key: "production",
        title: t("production"),
        featureFlag: "strategy_dashboard_production",
        permissionsToCheck: ["strategy_dashboard_production"],
        content: <LoadedProductionTab />,
      },
      {
        key: "quality",
        title: t("quality"),
        featureFlag: "strategy_dashboard_quality",
        permissionsToCheck: ["strategy_dashboard_quality"],
        content: <LoadedQualityTab />,
      },
      {
        key: "cost",
        title: t("cost"),
        featureFlag: "strategy_dashboard_cost",
        permissionsToCheck: ["strategy_dashboard_cost"],
        content: <LoadedCostTab />,
      },
      {
        key: "prices",
        title: t("prices"),
        featureFlag: "strategy_dashboard_prices",
        permissionsToCheck: ["strategy_dashboard_prices"],
        content: <LoadedPricesTab />,
      },
      {
        key: "heats",
        title: t("heats"),
        featureFlag: "strategy_dashboard_heats",
        permissionsToCheck: ["strategy_dashboard_heats"],
        content: <HeatsTab />,
      },
      {
        key: "scrapChefUsage",
        title: t("scrapChefUsage"),
        featureFlag: "strategy_dashboard_scrap_chef_usage",
        permissionsToCheck: ["strategy_dashboard_scrap_chef_usage"],
        ignoreCatchall: true,
        content: <LoadedScrapChefUsageTab />,
      },
      {
        key: "savings",
        title: t("savings"),
        featureFlag: "strategy_dashboard_savings",
        permissionsToCheck: ["strategy_dashboard_savings"],
        content: <LoadedSavingsTab />,
      },
      {
        key: "deliveries",
        title: t("deliveries"),
        featureFlag: "strategy_dashboard_deliveries",
        permissionsToCheck: ["strategy_dashboard_deliveries"],
        content: <LoadedDeliveriesTab />,
      },
      {
        key: "compliance",
        title: t("compliance"),
        featureFlag: "strategy_dashboard_compliance",
        permissionsToCheck: ["strategy_dashboard_compliance"],
        content: <LoadedComplianceTab />,
      },
      {
        key: "editing",
        title: t("editing"),
        featureFlag: "strategy_dashboard_editing",
        permissionsToCheck: ["strategy_dashboard_editing"],
        ignoreCatchall: true,
        content: <LoadedEditingTab />,
      },
      {
        key: "crewComparison",
        title: t("crewComparison"),
        featureFlag: "strategy_dashboard_crew_comparison",
        permissionsToCheck: ["strategy_dashboard_crew_comparison"],
        content: <CrewComparisonTab />,
      },
      {
        key: "schedule",
        title: t("schedule"),
        featureFlag: "strategy_dashboard_schedule",
        permissionsToCheck: ["strategy_dashboard_schedule"],
        content: <LoadedScheduleTab />,
      },
    ],
    [t]
  );

  const { feature_flags: tenantFeatureFlags } = useTenantData();
  const hasPermission = useHasPermission();
  const filteredTabs = useMemo(() => {
    return tabSpecs.filter(
      (tab) =>
        (tab.featureFlag
          ? tenantFeatureFlags.includes(tab.featureFlag)
          : true) &&
        (tab.permissionsToCheck
          ? tab.permissionsToCheck.every((permission) =>
              hasPermission(permission)
            ) ||
            (!tab.ignoreCatchall && hasPermission("analytics_admin"))
          : true)
    );
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- hasPermission returns a new function every render, breaking memoization
  }, [tabSpecs, tenantFeatureFlags]);

  const visibleTabKeys = useMemo(
    () => filteredTabs.map((t) => t.key),
    [filteredTabs]
  );

  const { selectedTabKey, activeTabKey, notFound } = useTabNavigation({
    tabSpecs: filteredTabs,
    defaultTabKey: filteredTabs[0]?.key,
    basePath: "/dashboard",
  });

  const tenantName = useTenant();

  useEffect(() => {
    if (activeTabKey) {
      void sendVisitNotification({
        path: `strategy_dashboard/${tenantName}/notify`,
        visit: { tab: activeTabKey },
      });
    }
  }, [activeTabKey, tenantName]);

  return (
    <DateRangeProvider config={DEFAULT_DATE_RANGE_CONFIG}>
      {notFound ? (
        <NotFound />
      ) : (
        <>
          <Stack
            sx={{ px: 2, pt: 2, pb: 2, minHeight: "100%", overflow: "auto" }}
          >
            <Typography variant="h1" sx={{ mb: 1 }}>
              {filteredTabs.find(({ key }) => key === selectedTabKey)?.title}
            </Typography>
            {filteredTabs.find(({ key }) => key === selectedTabKey)?.content}
          </Stack>
          <PrefetchStrategyDashboardData
            selectedTabKey={selectedTabKey}
            visibleTabKeys={visibleTabKeys}
          />
        </>
      )}
    </DateRangeProvider>
  );
};
